import { useApolloClient } from "@apollo/client";
import { getDataOrNull } from "@msys/common";
import {
  getFormattedPrice,
  isImageOr3dModel,
  Modal,
  processAttachment,
  useScreenWidth,
} from "@msys/ui";
import {
  Box,
  Button,
  DialogActions,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { useTolgee, useTranslate } from "@tolgee/react";
import { partition } from "lodash-es";
import React from "react";
import { GalleryRow } from "../../../commons/images/GalleryRow.js";
import { useExpandedStoreWithLocalStorage } from "../../../trees-virtual/hooks/useExpandedStore.js";
import { FilesBoxTable } from "../../attachments/FilesBoxTable.js";
import {
  templateQuoteItemHasDescription,
  TemplateQuotePreviewItemDescription,
} from "./TemplateQuotePreviewItemDescription.js";
import { TemplatesQuotePreviewItems } from "./TemplateQuotePreviewItems.js";
import {
  TemplatesQuoteOverviewModal_QuoteTemplateFragment,
  useTemplatesQuoteOverviewModal_LatestQuery,
  useTemplatesQuoteOverviewModal_VersionQuery,
} from "./TemplatesQuoteOverviewModal.generated.js";

interface Props {
  docId: string;
  versionNumber?: number | null;
}

export const TemplatesQuoteOverviewModal = ({
  docId,
  versionNumber,
  ...props
}: Props & TemplatesQuoteOverviewModalDocProps) => {
  return versionNumber ? (
    <TemplatesQuoteOverviewModalVersion
      docId={docId}
      versionNumber={versionNumber}
      {...props}
    />
  ) : (
    <TemplatesQuoteOverviewModalLatest docId={docId} {...props} />
  );
};

const TemplatesQuoteOverviewModalLatest = ({
  docId,
  ...props
}: {
  docId: string;
} & TemplatesQuoteOverviewModalDocProps) => {
  const client = useApolloClient();
  const query = useTemplatesQuoteOverviewModal_LatestQuery({
    client,
    variables: {
      id: docId,
    },
  });
  const doc =
    getDataOrNull(query?.data?.quoteTemplateLatest)?.quoteTemplate ?? null;
  return (
    <TemplatesQuoteOverviewModalDoc
      doc={doc}
      isLoading={query.loading}
      {...props}
    />
  );
};

const TemplatesQuoteOverviewModalVersion = ({
  docId,
  versionNumber,
  ...props
}: {
  docId: string;
  versionNumber: number;
} & TemplatesQuoteOverviewModalDocProps) => {
  const client = useApolloClient();
  const query = useTemplatesQuoteOverviewModal_VersionQuery({
    client,
    variables: {
      id: docId,
      version: versionNumber,
      applyOverrides: false,
    },
  });
  const doc =
    getDataOrNull(query?.data?.quoteTemplateVersion)?.quoteTemplate ?? null;
  return (
    <TemplatesQuoteOverviewModalDoc
      doc={doc}
      isLoading={query.loading}
      {...props}
    />
  );
};

interface TemplatesQuoteOverviewModalDocProps {
  headerActions?: React.ReactNode;
  handleClose: () => void;
  dialogActionButton?: React.ReactNode;
  canEdit?: boolean;
  notInStack?: boolean;
}

const TemplatesQuoteOverviewModalDoc = ({
  doc,
  isLoading = false,
  handleClose,
  headerActions,
  dialogActionButton,
  notInStack,
}: {
  doc: TemplatesQuoteOverviewModal_QuoteTemplateFragment | null;
  isLoading?: boolean;
} & TemplatesQuoteOverviewModalDocProps) => {
  const { isMinTablet, isMinDesktop } = useScreenWidth();
  const { t } = useTranslate(["Shop", "Global", "Quote"]);
  const language = useTolgee(["language"]).getLanguage()!;

  const expandedStore = useExpandedStoreWithLocalStorage(
    "quote-template",
    doc ? `${doc.id}-${doc.resolvedAsReadModelVersionNumber ?? "latest"}` : ""
  );

  const rootItem = doc?.rootItem;

  const [images, files] = partition(
    (rootItem?.attachments ?? [])
      .filter(attachment => attachment.clientVisibility)
      .map(processAttachment),
    isImageOr3dModel
  );

  return (
    <Modal
      title={doc?.title}
      handleClose={handleClose}
      maxWidth="md"
      notInStack={notInStack}
      isLoading={isLoading}
      headerActions={headerActions}
      dialogActions={
        <DialogActions>
          <Stack
            direction={isMinTablet ? "row" : "column"}
            spacing={2}
            sx={{
              alignItems: "center",
            }}
          >
            {doc && (
              <Box>
                <Typography
                  variant="h4"
                  component="div"
                  align={isMinTablet ? "right" : "left"}
                >
                  {t("Price", { ns: "Shop" })}:{" "}
                  {getFormattedPrice(
                    doc.proposedCalculation?.priceTotal ?? 0,
                    language
                  )}
                </Typography>
                <Typography
                  variant="caption"
                  component="div"
                  align={isMinTablet ? "right" : "left"}
                  sx={{
                    color: "grey.500",
                  }}
                >
                  {t(
                    "Includes VAT. Final price in the quote may be different depending on selected options.",
                    {
                      ns: "Shop",
                    }
                  )}
                </Typography>
              </Box>
            )}
            <Stack
              direction="row"
              spacing={1}
              sx={{
                alignItems: "center",
                alignSelf: isMinTablet ? undefined : "flex-end",
              }}
            >
              <Button onClick={handleClose} color="primary" variant="text">
                {t("Close", {
                  ns: "Global",
                })}
              </Button>
              {dialogActionButton}
            </Stack>
          </Stack>
        </DialogActions>
      }
    >
      {doc && rootItem && (
        <Stack
          direction="column"
          spacing={1}
          sx={{
            width: "100%",
          }}
        >
          {(images.length > 0 || templateQuoteItemHasDescription(rootItem)) && (
            <Paper>
              <Stack
                spacing={2}
                sx={{
                  padding: 2,
                  flexDirection: "column",
                }}
              >
                <GalleryRow
                  images={images}
                  maxItems={isMinDesktop ? 8 : isMinTablet ? 6 : 4}
                />

                <TemplateQuotePreviewItemDescription
                  item={rootItem}
                  columns={isMinDesktop ? 4 : isMinTablet ? 2 : 1}
                />
              </Stack>
            </Paper>
          )}

          <TemplatesQuotePreviewItems
            canEdit={false}
            docId={doc.id}
            allDocItems={doc.items}
            contractType={doc.contractType}
            isBinding={doc.isBinding}
            expandedStore={expandedStore}
          />

          {files.length > 0 && (
            <FilesBoxTable
              title={t("Attachments", { ns: "Quote" })}
              attachments={files}
              canEdit={false}
              gridColumns={isMinDesktop ? 8 : isMinTablet ? 6 : 4}
            />
          )}
        </Stack>
      )}
    </Modal>
  );
};
